import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  AcademicCapIcon,
  PlayIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ListBulletIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import { Card, Badge, InlineSpinner, EmptyState, Button } from '@/components/ui'
import { lmsApi } from '@/api'
import type { Lesson, Course, Module } from '@/api/lms'
import TrackedVideoPlayer from '@/components/lms/TrackedVideoPlayer'
import ScormPlayer from '@/components/lms/ScormPlayer'

type CourseWithModules = Course & { modules: Module[] }

export default function LessonPage() {
  const { slug, lessonId } = useParams<{ slug: string; lessonId: string }>()
  const navigate = useNavigate()
  const [course, setCourse] = useState<CourseWithModules | null>(null)
  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [loading, setLoading] = useState(true)
  const [completing, setCompleting] = useState(false)
  const [showSidebar, setShowSidebar] = useState(true)
  const [lockedMessage, setLockedMessage] = useState<string | null>(null)

  useEffect(() => {
    if (slug && lessonId) {
      fetchData()
    }
  }, [slug, lessonId])

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const [courseData, lessonData] = await Promise.all([
        lmsApi.getCourse(slug!),
        lmsApi.getLesson(Number(lessonId)).catch((err) => {
          // Sequential course: the previous module must be completed first
          if (err.response?.data?.locked) {
            setLockedMessage(err.response.data.error)
            return null
          }
          throw err
        }),
      ])
      setCourse(courseData)
      setLesson(lessonData)
      if (lessonData) setLockedMessage(null)
      // Debug: log lesson data
      console.log('Lesson data received:', lessonData)
      console.log('Lesson type:', lessonData?.type)
      console.log('Lesson video_url:', lessonData?.video_url)
      console.log('Lesson file_url:', lessonData?.file_url)
      console.log('Lesson file:', lessonData?.file)
    } catch (error) {
      console.error('Error fetching lesson data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkComplete = async () => {
    if (!lesson) return
    setCompleting(true)
    try {
      await lmsApi.markLessonComplete(lesson.id)
      // Refresh data
      await fetchData()
    } catch (error) {
      console.error('Error marking lesson complete:', error)
    } finally {
      setCompleting(false)
    }
  }

  const findAdjacentLessons = () => {
    if (!course || !lesson) return { prev: null, next: null }

    const allLessons: { lesson: Lesson; moduleIndex: number; moduleLocked: boolean }[] = []
    course.modules.forEach((mod, modIdx) => {
      mod.lessons.forEach((les) => {
        allLessons.push({
          lesson: les,
          moduleIndex: modIdx,
          moduleLocked: !!mod.is_locked && !les.is_completed,
        })
      })
    })

    const currentIndex = allLessons.findIndex((l) => l.lesson.id === lesson.id)
    const nextItem = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null

    return {
      prev: currentIndex > 0 ? allLessons[currentIndex - 1].lesson : null,
      next: nextItem ? nextItem.lesson : null,
      nextModuleLocked: !!nextItem?.moduleLocked,
    }
  }

  const { prev, next, nextModuleLocked } = findAdjacentLessons()
  // The next lesson cannot be opened until what unlocks it has been completed
  const nextLocked = nextModuleLocked || (!!next?.is_locked && !next.is_completed)
  // Wording depends on what the locked lesson is waiting for (videos or other content)
  const waitsOnlyForVideos = (item?: Lesson | null) =>
    (item?.blocking_lessons || []).every((blocking) => blocking.type === 'video')

  const getCurrentModuleAndPosition = () => {
    if (!course || !lesson) return { module: null, position: '' }

    for (let modIdx = 0; modIdx < course.modules.length; modIdx++) {
      const mod = course.modules[modIdx]
      const lessonIdx = mod.lessons.findIndex((l) => l.id === lesson.id)
      if (lessonIdx !== -1) {
        return {
          module: mod,
          position: `${modIdx + 1}.${lessonIdx + 1}`,
        }
      }
    }
    return { module: null, position: '' }
  }

  const { module: currentModule, position } = getCurrentModuleAndPosition()

  const mustWatchVideo =
    lesson?.type === 'video' && !!lesson.require_full_view && !!(lesson.file_url || lesson.file)
  // Le lezioni con questionario si completano solo superando il quiz
  const mustPassQuiz = lesson?.type === 'quiz' || !!lesson?.quiz
  // SCORM lessons are completed by the content itself
  const mustCompleteScorm = lesson?.type === 'scorm'
  const canMarkComplete = !mustWatchVideo && !mustPassQuiz && !mustCompleteScorm && !lesson?.is_locked

  if (loading) {
    return <InlineSpinner message="Caricamento lezione..." />
  }

  if (lockedMessage && !lesson) {
    return (
      <EmptyState
        icon={<LockClosedIcon className="h-12 w-12" />}
        title="Modulo bloccato"
        description={lockedMessage}
        action={{
          label: 'Torna al corso',
          onClick: () => navigate(`/courses/${slug}`),
        }}
      />
    )
  }

  if (!course || !lesson) {
    return (
      <EmptyState
        icon={<AcademicCapIcon className="h-12 w-12" />}
        title="Lezione non trovata"
        description="La lezione richiesta non esiste"
        action={{
          label: 'Torna al corso',
          onClick: () => navigate(`/courses/${slug}`),
        }}
      />
    )
  }

  return (
    <div className="flex h-[calc(100vh-120px)]">
      {/* Sidebar - Course Navigation */}
      <div className={`${showSidebar ? 'w-80' : 'w-0'} flex-shrink-0 bg-white border-r border-gray-200 overflow-hidden transition-all`}>
        <div className="h-full flex flex-col">
          {/* Course Header */}
          <div className="p-4 border-b border-gray-200">
            <Link
              to={`/courses/${slug}`}
              className="flex items-center text-sm text-gray-500 hover:text-primary-600 mb-2"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Torna al corso
            </Link>
            <h2 className="font-semibold text-gray-900 line-clamp-2">{course.title}</h2>
          </div>

          {/* Modules List */}
          <div className="flex-1 overflow-y-auto">
            {course.modules.map((mod, modIdx) => (
              <div key={mod.id}>
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="flex items-center text-sm font-medium text-gray-700">
                    {mod.is_locked && (
                      <LockClosedIcon className="h-4 w-4 mr-1 flex-shrink-0 text-gray-400" />
                    )}
                    Modulo {modIdx + 1}: {mod.title}
                  </h3>
                  {mod.is_locked && mod.blocking_module && (
                    <p className="text-xs text-gray-500 mt-1">
                      Si sblocca completando "{mod.blocking_module.title}"
                    </p>
                  )}
                </div>
                <div className="divide-y divide-gray-100">
                  {mod.lessons.map((les, lesIdx) => (
                    <button
                      key={les.id}
                      onClick={() => navigate(`/courses/${slug}/lesson/${les.id}`)}
                      disabled={(!!mod.is_locked || !!les.is_locked) && !les.is_completed && les.id !== lesson.id}
                      title={
                        (!!mod.is_locked || !!les.is_locked) && !les.is_completed
                          ? 'Lezione bloccata: completa prima le lezioni precedenti'
                          : undefined
                      }
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white ${
                        les.id === lesson.id ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                      }`}
                    >
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                        les.is_completed
                          ? 'bg-green-100 text-green-600'
                          : les.id === lesson.id
                          ? 'bg-primary-100 text-primary-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {les.is_completed ? (
                          <CheckCircleSolidIcon className="h-4 w-4" />
                        ) : les.is_locked ? (
                          <LockClosedIcon className="h-3.5 w-3.5" />
                        ) : (
                          <span className="text-xs font-medium">{lesIdx + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${
                          les.id === lesson.id
                            ? 'font-medium text-primary-700'
                            : les.is_completed
                            ? 'text-gray-500'
                            : 'text-gray-900'
                        }`}>
                          {les.title}
                        </p>
                        <p className="text-xs text-gray-400">
                          {les.duration_minutes} min • {les.type}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ListBulletIcon className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <nav className="flex items-center text-sm text-gray-500">
                <span>{currentModule?.title}</span>
                <ChevronRightIcon className="h-4 w-4 mx-1" />
                <span className="text-gray-900">Lezione {position}</span>
              </nav>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {lesson.is_completed ? (
              <Badge variant="success" size="lg">
                <CheckCircleSolidIcon className="h-4 w-4 mr-1" />
                Completata
              </Badge>
            ) : canMarkComplete ? (
              <Button
                size="sm"
                onClick={handleMarkComplete}
                disabled={completing}
              >
                {completing ? 'Completamento...' : 'Segna come completata'}
              </Button>
            ) : (
              <Badge variant="warning" size="lg">
                {(mustWatchVideo || lesson.is_locked) && <LockClosedIcon className="h-4 w-4 mr-1" />}
                {mustWatchVideo
                  ? 'Visione obbligatoria'
                  : lesson.is_locked
                  ? 'Bloccato'
                  : mustCompleteScorm
                  ? 'Contenuto da completare'
                  : 'Questionario da superare'}
              </Badge>
            )}
          </div>
        </div>

        {/* Lesson Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {/* Lesson Title */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge size="sm" className="capitalize">{lesson.type}</Badge>
                {lesson.duration_minutes > 0 && (
                  <span className="text-sm text-gray-500">
                    {lesson.duration_minutes} minuti
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{lesson.title}</h1>
              {lesson.description && (
                <p className="text-gray-600 mt-2">{lesson.description}</p>
              )}
            </div>

            {/* Content by Type */}
            <Card className="mb-6">
              {/* VIDEO - Handle both uploaded files and external URLs (YouTube, Vimeo) */}
              {lesson.type === 'video' && (
                <>
                  {(() => {
                    const videoUrl = lesson.video_url || lesson.file_url || lesson.file
                    if (!videoUrl) return null

                    // Check if it's a YouTube URL
                    const youtubeMatch = videoUrl.match(
                      /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
                    )
                    if (youtubeMatch) {
                      return (
                        <div className="aspect-video bg-black rounded-lg overflow-hidden">
                          <iframe
                            src={`https://www.youtube.com/embed/${youtubeMatch[1]}`}
                            title={lesson.title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )
                    }

                    // Check if it's a Vimeo URL
                    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/)
                    if (vimeoMatch) {
                      return (
                        <div className="aspect-video bg-black rounded-lg overflow-hidden">
                          <iframe
                            src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
                            title={lesson.title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )
                    }

                    // Video with mandatory full view: tracked player
                    if (mustWatchVideo) {
                      return (
                        <TrackedVideoPlayer
                          key={lesson.id}
                          lessonId={lesson.id}
                          src={lesson.file_url || lesson.file || videoUrl}
                          poster={course.thumbnail_url || course.thumbnail || undefined}
                          initialPosition={lesson.video_progress?.position_seconds || 0}
                          initialWatched={lesson.video_progress?.watched_seconds || 0}
                          completed={!!lesson.is_completed}
                          onCompleted={() => fetchData(true)}
                        />
                      )
                    }

                    // Regular video file
                    return (
                      <div className="aspect-video bg-black rounded-lg overflow-hidden">
                        <video
                          src={videoUrl}
                          controls
                          className="w-full h-full"
                          poster={course.thumbnail_url || course.thumbnail || undefined}
                        >
                          Il tuo browser non supporta la riproduzione video.
                        </video>
                      </div>
                    )
                  })()}
                </>
              )}

              {/* TEXT or HTML - Article content */}
              {(lesson.type === 'text' || lesson.type === 'html') && lesson.content && (
                <div
                  className="prose prose-lg max-w-none p-6"
                  dangerouslySetInnerHTML={{ __html: lesson.content }}
                />
              )}

              {/* PDF - Embed or download */}
              {lesson.type === 'pdf' && (lesson.file_url || lesson.file) && (
                <div>
                  {/* Try to embed PDF first */}
                  <div className="mb-4">
                    <iframe
                      src={lesson.file_url || lesson.file || ''}
                      className="w-full h-[600px] border-0 rounded-lg"
                      title={lesson.title}
                    />
                  </div>
                  <div className="text-center py-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500 mb-2">
                      Se il documento non viene visualizzato correttamente:
                    </p>
                    <a
                      href={lesson.file_url || lesson.file || ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Apri PDF in nuova scheda
                    </a>
                  </div>
                </div>
              )}

              {/* PRESENTATION - PowerPoint/Slides */}
              {lesson.type === 'presentation' && (lesson.file_url || lesson.file) && (
                <div>
                  {/* Check if it's a PDF presentation */}
                  {(lesson.file_url || lesson.file || '').toLowerCase().endsWith('.pdf') ? (
                    <div>
                      <iframe
                        src={lesson.file_url || lesson.file || ''}
                        className="w-full h-[600px] border-0 rounded-lg"
                        title={lesson.title}
                      />
                      <div className="text-center py-4 border-t border-gray-200">
                        <a
                          href={lesson.file_url || lesson.file || ''}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          Apri presentazione in nuova scheda
                        </a>
                      </div>
                    </div>
                  ) : (
                    /* For PPTX files, offer download */
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 2a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H6zm7 1.5L18.5 9H13V3.5zM8 11h8v2H8v-2zm0 4h8v2H8v-2z"/>
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Presentazione</h3>
                      <p className="text-gray-600 mb-4">
                        Scarica la presentazione per visualizzarla
                      </p>
                      <a
                        href={lesson.file_url || lesson.file || ''}
                        download
                        className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Scarica Presentazione
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* RESOURCE - Downloadable file */}
              {lesson.type === 'resource' && (lesson.file_url || lesson.file) && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{lesson.title}</h3>
                  <p className="text-gray-600 mb-4">
                    Materiale didattico disponibile per il download
                  </p>
                  <a
                    href={lesson.file_url || lesson.file || ''}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Scarica Risorsa
                  </a>
                  {lesson.content && (
                    <div className="mt-6 text-left p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">Descrizione:</h4>
                      <p className="text-gray-600">{lesson.content}</p>
                    </div>
                  )}
                </div>
              )}

              {/* QUIZ - locked until the mandatory videos have been watched */}
              {lesson.type === 'quiz' && lesson.is_locked && (
                <div className="text-center py-8">
                  <LockClosedIcon className="h-16 w-16 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Questionario bloccato</h3>
                  <p className="text-gray-600 mb-4">
                    {waitsOnlyForVideos(lesson)
                      ? 'Per sbloccare il questionario devi prima guardare fino alla fine:'
                      : 'Per sbloccare il questionario devi prima completare:'}
                  </p>
                  <ul className="inline-block text-left space-y-2">
                    {(lesson.blocking_lessons || []).map((blocking) => (
                      <li key={blocking.id}>
                        <Link
                          to={`/courses/${slug}/lesson/${blocking.id}`}
                          className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                        >
                          <PlayIcon className="h-5 w-5 mr-2" />
                          {blocking.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* QUIZ */}
              {lesson.type === 'quiz' && !lesson.is_locked && (
                <div className="text-center py-8">
                  <AcademicCapIcon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Quiz di verifica</h3>
                  <p className="text-gray-600 mb-4">
                    Metti alla prova le tue conoscenze con questo quiz
                  </p>
                  <Button onClick={() => navigate(`/courses/${slug}/lesson/${lessonId}/quiz`)}>
                    <PlayIcon className="h-5 w-5 mr-2" />
                    Inizia Quiz
                  </Button>
                </div>
              )}

              {/* SCORM */}
              {lesson.type === 'scorm' && lesson.scorm?.available && (
                <ScormPlayer
                  key={lesson.id}
                  lessonId={lesson.id}
                  completed={!!lesson.is_completed}
                  initialStatus={lesson.scorm.status}
                  initialScore={lesson.scorm.score}
                  onCompleted={() => fetchData(true)}
                />
              )}
              {lesson.type === 'scorm' && !lesson.scorm?.available && (
                <div className="text-center py-8">
                  <AcademicCapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Contenuto interattivo</h3>
                  <p className="text-gray-600">
                    Il pacchetto SCORM di questa lezione non è ancora stato caricato.
                  </p>
                </div>
              )}

              {/* No content available */}
              {!lesson.content && !lesson.video_url && !lesson.file_url && !lesson.file && lesson.type !== 'quiz' && lesson.type !== 'scorm' && (
                <div className="text-center py-8 text-gray-500">
                  Contenuto non disponibile
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-gray-200">
          <div>
            {prev ? (
              <Button
                variant="outline"
                onClick={() => navigate(`/courses/${slug}/lesson/${prev.id}`)}
              >
                <ChevronLeftIcon className="h-5 w-5 mr-2" />
                Lezione precedente
              </Button>
            ) : (
              <div />
            )}
          </div>

          <div className="text-center">
            {!lesson.is_completed && canMarkComplete && (
              <Button
                onClick={handleMarkComplete}
                disabled={completing}
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                {completing ? 'Completamento...' : 'Completa e continua'}
              </Button>
            )}
            {!lesson.is_completed && mustWatchVideo && (
              <p className="text-sm text-gray-500">
                La lezione si completa guardando il video fino alla fine
              </p>
            )}
            {!lesson.is_completed && mustCompleteScorm && (
              <p className="text-sm text-gray-500">
                La lezione si completa portando a termine il contenuto interattivo
              </p>
            )}
            {!lesson.is_completed && mustPassQuiz && !lesson.is_locked && (
              <p className="text-sm text-gray-500">
                La lezione si completa superando il questionario
              </p>
            )}
          </div>

          <div>
            {next ? (
              <Button
                onClick={() => navigate(`/courses/${slug}/lesson/${next.id}`)}
                disabled={nextLocked}
                title={
                  nextModuleLocked
                    ? 'Completa tutte le lezioni di questo modulo per sbloccare il successivo'
                    : nextLocked
                    ? waitsOnlyForVideos(next)
                      ? 'Guarda il video fino alla fine per sbloccare la lezione successiva'
                      : 'Completa questa lezione per sbloccare la successiva'
                    : undefined
                }
              >
                {nextLocked && <LockClosedIcon className="h-5 w-5 mr-2" />}
                {nextModuleLocked ? 'Modulo successivo bloccato' : 'Lezione successiva'}
                {!nextLocked && <ChevronRightIcon className="h-5 w-5 ml-2" />}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate(`/courses/${slug}`)}
              >
                Torna al corso
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
